import React from "react";
import {
  Paintbrush,
  IndianRupee,
  CheckCircle2,
  ArrowRight,
  Home,
  Building2,
  ShieldCheck,
  Users,
  Star,
  Clock3,
  Brush,
} from "lucide-react";

const PaintingServices = () => {
  const benefits = [
    "Starting at just ₹7 per sq.ft.",
    "Interior & Exterior Painting",
    "Free Site Inspection & Quote",
    "Experienced & Verified Painters",
    "Clean, Fast & Hassle-Free Service",
    "10–15 Projects Completed Daily",
    "Trusted by 1000+ Property Owners",
    "Premium Quality Paints & Finish",
  ];

  const services = [
    {
      title: "Interior Painting",
      icon: <Home className="w-8 h-8" />,
    },
    {
      title: "Exterior Painting",
      icon: <Building2 className="w-8 h-8" />,
    },
    {
      title: "Apartment Painting",
      icon: <Paintbrush className="w-8 h-8" />,
    },
    {
      title: "Villa Painting",
      icon: <Home className="w-8 h-8" />,
    },
    {
      title: "Office Painting",
      icon: <Building2 className="w-8 h-8" />,
    },
    {
      title: "Rental Property Painting",
      icon: <Brush className="w-8 h-8" />,
    },
    {
      title: "Repainting Services",
      icon: <Paintbrush className="w-8 h-8" />,
    },
    {
      title: "Premium Texture Finishes",
      icon: <Star className="w-8 h-8" />,
    },
  ];

  const stats = [
    {
      icon: <Users className="w-6 h-6" />,
      value: "1000+",
      label: "Happy Property Owners",
    },
    {
      icon: <Paintbrush className="w-6 h-6" />,
      value: "10-15",
      label: "Projects Completed Daily",
    },
    {
      icon: <ShieldCheck className="w-6 h-6" />,
      value: "100%",
      label: "Professional Finish",
    },
  ];

  const process = [
    {
      step: "01",
      title: "Free Inspection",
      desc: "Schedule a site visit with our painting experts.",
    },
    {
      step: "02",
      title: "Get Quotation",
      desc: "Receive a transparent and detailed price estimate.",
    },
    {
      step: "03",
      title: "Painting Work",
      desc: "Professional painters complete the work efficiently.",
    },
    {
      step: "04",
      title: "Final Handover",
      desc: "Quality check and spotless project completion.",
    },
  ];

  return (
    <section className="relative overflow-hidden bg-gradient-to-br from-[#F8FAFC] via-white to-[#EEF2FF] py-24 px-4 md:px-10">
      
      {/* Background Blur Effects */}
      <div className="absolute top-0 left-0 w-96 h-96 bg-red-100 rounded-full blur-3xl opacity-40"></div>
      <div className="absolute bottom-0 right-0 w-[30rem] h-[30rem] bg-green-100 rounded-full blur-3xl opacity-40"></div>

      <div className="relative z-10 max-w-7xl mx-auto">

        {/* Hero Section */}
        <div className="text-center max-w-4xl mx-auto mb-20">

          <div className="inline-flex items-center gap-2 bg-white border border-red-100 shadow-md px-5 py-2 rounded-full mb-6">
            <Paintbrush className="w-4 h-4 text-red-500" />
            <span className="text-sm font-semibold uppercase tracking-[0.2em] text-red-600">
              Painting Services Bangalore
            </span>
          </div>

          <h1 className="text-5xl md:text-7xl font-black text-gray-900 leading-tight">
            Professional
            <span className="block text-transparent bg-clip-text bg-gradient-to-r from-[#E63946] via-[#ff4d5a] to-[#ff7b7b]">
              Painting Services
            </span>
          </h1>

          <p className="mt-8 text-lg md:text-xl text-gray-600 leading-relaxed max-w-3xl mx-auto">
            Give your home a fresh new look with professional painting
            services from Housyhunt. Trusted by homeowners, landlords,
            and property investors across Bangalore.
          </p>
        </div>

        {/* Hero Content */}
        <div className="grid lg:grid-cols-2 gap-14 items-center">

          {/* Left Content */}
          <div>

            <div className="inline-flex items-center gap-3 bg-green-50 border border-green-100 px-5 py-3 rounded-2xl mb-8">
              <IndianRupee className="w-5 h-5 text-green-600" />
              <span className="font-bold text-green-700">
                Starting From ₹7 / Sq.Ft.
              </span>
            </div>

            <h2 className="text-4xl md:text-5xl font-black text-gray-900 leading-tight">
              Transform Your Property
              <span className="block text-[#E63946]">
                With Professional Painting
              </span>
            </h2>

            <p className="text-lg text-gray-600 mt-6 leading-relaxed">
              Whether it's an apartment, villa, office, rental property,
              or resale unit, Housyhunt ensures quality workmanship,
              timely completion, and hassle-free execution.
            </p>

            <div className="grid sm:grid-cols-2 gap-4 mt-10">
              {benefits.map((item, index) => (
                <div
                  key={index}
                  className="flex items-center gap-4 bg-white border border-gray-100 rounded-2xl p-4 hover:shadow-xl transition-all duration-300"
                >
                  <div className="bg-red-50 p-2 rounded-xl text-red-500">
                    <CheckCircle2 className="w-5 h-5" />
                  </div>

                  <span className="font-medium text-gray-700">
                    {item}
                  </span>
                </div>
              ))}
            </div>

            <button className="group mt-10 bg-gradient-to-r from-[#E63946] to-[#ff5f6d] text-white px-8 py-4 rounded-2xl font-bold shadow-xl hover:scale-105 transition-all duration-300 flex items-center gap-3">
              Get Free Inspection
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </button>
          </div>

          {/* Right Image */}
          <div className="relative">

            <div className="relative rounded-[2.5rem] overflow-hidden shadow-[0_25px_80px_rgba(0,0,0,0.12)] border border-white">

              <img
                src="https://images.unsplash.com/photo-1562259949-e8e7689d7828?q=80&w=1600&auto=format&fit=crop"
                alt="House Painting Service"
                className="w-full h-[700px] object-cover"
              />

              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent"></div>

              <div className="absolute top-6 left-6 bg-white/90 backdrop-blur-xl rounded-2xl px-5 py-3 shadow-xl">
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-red-500">
                  Bangalore Exclusive
                </p>

                <h4 className="font-semibold text-gray-900 mt-1">
                  Professional Painting Solutions
                </h4>
              </div>

              <div className="absolute bottom-8 left-8 right-8">
                <h3 className="text-4xl font-black text-white">
                  Fresh Walls. Fresh Start.
                </h3>

                <p className="text-gray-200 text-lg mt-4 leading-relaxed">
                  Premium painting services with quality materials,
                  expert painters and timely completion.
                </p>
              </div>
            </div>

            <div className="hidden md:block absolute -bottom-8 -left-8 bg-white/90 backdrop-blur-2xl rounded-3xl p-6 shadow-2xl w-72 border border-white">
              <div className="flex items-center gap-4">
                <div className="bg-green-100 text-green-600 p-4 rounded-2xl">
                  <Paintbrush className="w-6 h-6" />
                </div>

                <div>
                  <h4 className="font-bold text-gray-900">
                    Free Site Inspection
                  </h4>

                  <p className="text-gray-500 text-sm mt-1">
                    Professional assessment and quotation.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Services */}
        <div className="mt-24">

          <div className="text-center mb-12">
            <h2 className="text-4xl font-black text-gray-900">
              Painting Solutions For Every Property
            </h2>

            <p className="text-gray-600 text-lg mt-4">
              Complete painting services for residential and commercial spaces.
            </p>
          </div>

          <div className="grid md:grid-cols-4 sm:grid-cols-2 gap-6">
            {services.map((service, index) => (
              <div
                key={index}
                className="bg-white border border-gray-100 rounded-3xl p-8 text-center hover:shadow-2xl hover:-translate-y-2 transition-all duration-300"
              >
                <div className="text-red-500 flex justify-center mb-4">
                  {service.icon}
                </div>

                <h3 className="font-bold text-xl text-gray-900">
                  {service.title}
                </h3>
              </div>
            ))}
          </div>
        </div>

        {/* Process */}
        <div className="mt-24">

          <div className="text-center mb-14">
            <h2 className="text-4xl font-black text-gray-900">
              Simple 4-Step Process
            </h2>
          </div>

          <div className="grid md:grid-cols-4 gap-6">
            {process.map((item, index) => (
              <div
                key={index}
                className="bg-white border border-gray-100 rounded-3xl p-8 shadow-lg"
              >
                <span className="text-5xl font-black text-red-100">
                  {item.step}
                </span>

                <h3 className="mt-4 text-2xl font-bold text-gray-900">
                  {item.title}
                </h3>

                <p className="mt-3 text-gray-600">
                  {item.desc}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Stats */}
        <div className="mt-24 bg-white/80 backdrop-blur-2xl border border-white rounded-[2.5rem] p-10 shadow-xl">

          <div className="grid md:grid-cols-3 gap-8">
            {stats.map((item, index) => (
              <div key={index} className="text-center">

                <div className="w-16 h-16 rounded-2xl bg-red-50 text-red-500 flex items-center justify-center mx-auto mb-4">
                  {item.icon}
                </div>

                <h3 className="text-5xl font-black text-gray-900">
                  {item.value}
                </h3>

                <p className="text-gray-600 mt-2 font-medium">
                  {item.label}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Final CTA */}
        <div className="mt-24 text-center">

          <div className="inline-flex items-center gap-2 bg-red-50 border border-red-100 px-5 py-2 rounded-full mb-6">
            <Star className="w-4 h-4 text-red-500" />
            <span className="font-semibold text-red-600">
              Bangalore Only
            </span>
          </div>

          <h2 className="text-4xl md:text-5xl font-black text-gray-900">
            Trusted by Owners.
            <span className="block text-[#E63946]">
              Delivered by Professionals.
            </span>
          </h2>

          <p className="text-lg text-gray-600 mt-6 max-w-2xl mx-auto">
            Interior, Exterior, Apartment, Villa and Commercial
            Painting Services Across Bangalore.
          </p>

          <button className="mt-10 bg-[#E63946] hover:bg-[#d42f3c] text-white px-8 py-4 rounded-2xl font-bold shadow-xl transition-all duration-300">
            Get Free Inspection
          </button>
        </div>
      </div>
    </section>
  );
};

export default PaintingServices;